import { threadApi } from '@/lib/api/threads.api'
import { createLogger } from '@/lib/logger'
import { isEntityActive, isEntityVisible } from '@/lib/realtime'
import { generateStableMessageId } from '@/utils/reaction-helpers'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import {
  CommentReactionAddedPayload,
  CommentReactionRemovedPayload,
  ModuleTypeEnum,
  ReactionGroup,
  ReactionReactor,
  SocketEvent,
  Thread,
  ThreadComment,
  ThreadCommentReaction,
  ThreadReaction,
  ThreadReactionAddedPayload,
  ThreadReactionRemovedPayload,
  unifiedToNative,
} from '@scrambl/shared'

import { useOrganizationStore } from './organization.store'
import { useThreadUIStore } from './thread-ui.store'

const logger = createLogger('store/thread.store')

// Helper to safely convert zod-inferred reaction groups to proper ReactionGroup[]
function normalizeReactionGroups(
  groups: unknown[] | undefined,
): ReactionGroup[] {
  if (!groups || !Array.isArray(groups)) return []
  return groups.map(g => {
    const group = g as Record<string, unknown>
    return {
      emoji: String(group.emoji || ''),
      nativeEmoji: String(group.nativeEmoji || ''),
      count: Number(group.count || 0),
      hasReacted: Boolean(group.hasReacted),
      reactors: Array.isArray(group.reactors)
        ? group.reactors.map((r: Record<string, unknown>) => ({
            id: String(r.id || ''),
            displayName: String(r.displayName || 'Unknown'),
            image: r.image as string | null | undefined,
          }))
        : [],
    } as ReactionGroup
  })
}

// Helper to create a reactor from payload creator
function createReactorFromPayload(
  creator:
    | { id?: string; displayName?: string | null; image?: string | null }
    | undefined,
  fallbackId: string,
): ReactionReactor {
  return {
    id: creator?.id || fallbackId,
    displayName: creator?.displayName || 'Unknown',
    image: creator?.image || null,
  }
}

// For development debugging
const isBrowser =
  typeof window !== 'undefined' && typeof localStorage !== 'undefined'

const DEBUG_MODE =
  import.meta.env.DEV &&
  isBrowser &&
  (localStorage.getItem('REALTIME_DEBUG') === 'true' ||
    localStorage.getItem('DEBUG_ALL') === 'true' ||
    localStorage.getItem('DEBUG_STORE') === 'true')
// Helper function for conditional logging
function debugLog(...args: any[]) {
  if (DEBUG_MODE) {
  }
}

function compareThreadComments(
  a: ThreadComment,
  b: ThreadComment,
  order: 'asc' | 'desc',
) {
  const aSequence = typeof a.sequence === 'number' ? a.sequence : null
  const bSequence = typeof b.sequence === 'number' ? b.sequence : null

  if (aSequence !== null && bSequence !== null && aSequence !== bSequence) {
    return order === 'asc' ? aSequence - bSequence : bSequence - aSequence
  }

  const aTime = new Date(a.createdAt).getTime()
  const bTime = new Date(b.createdAt).getTime()
  return order === 'asc' ? aTime - bTime : bTime - aTime
}

// ThreadFilters type is now exported from thread-filters.store.ts

type ThreadCommentWithLocal = ThreadComment & {
  localStatus?: 'pending' | 'failed'
}

interface ExtendedThread extends Thread {
  _lastAddedCommentId?: string
}

interface ThreadWithMetadata extends Thread {
  _lastAddedCommentId?: string
}

interface CommentCacheEntry {
  comments: ThreadComment[]
  timestamp: number
  totalComments: number
  isLoading: boolean
}

const threadCommentCache = new Map<string, CommentCacheEntry>()
const COMMENT_CACHE_TTL = 30 * 60 * 1000 // 30 minutes

const buildThreadCommentCacheKey = (
  threadId: string,
  parentCommentId: string | null | undefined,
  page: number,
  limit: number,
) => {
  return `thread:${threadId}:parent:${parentCommentId === null ? 'root' : parentCommentId || 'all'}:page:${page}:limit:${limit}`
}

// Track recently added comments to prevent duplicates (local + socket)
const recentlyAddedComments = new Map<string, number>()
const RECENT_COMMENT_TTL = 10000 // 10 seconds

// Ref-counted registry of threads whose comments are currently rendered by a
// mounted component. cleanupThreads must never sweep these. This is the
// ground truth for "is this thread on screen" — isEntityActive/isEntityVisible
// only reflect socket room membership, which drops on reconnects and is empty
// when the socket was not ready at mount, so relying on it made the 2-minute
// sweep delete comments out from under an open task sheet.
const renderedThreadRefCounts = new Map<string, number>()

/**
 * Mark a thread's comments as rendered by a mounted component. Returns a
 * release function to call on unmount.
 */
export function retainThreadComments(threadId: string): () => void {
  renderedThreadRefCounts.set(
    threadId,
    (renderedThreadRefCounts.get(threadId) || 0) + 1,
  )
  let released = false
  return () => {
    if (released) return
    released = true
    const next = (renderedThreadRefCounts.get(threadId) || 1) - 1
    if (next <= 0) {
      renderedThreadRefCounts.delete(threadId)
    } else {
      renderedThreadRefCounts.set(threadId, next)
    }
  }
}

// Clean up the comment cache periodically
if (typeof window !== 'undefined') {
  setInterval(() => {
    const now = Date.now()

    // Clean up comment cache
    threadCommentCache.forEach((entry, threadId) => {
      if (now - entry.timestamp > COMMENT_CACHE_TTL) {
        threadCommentCache.delete(threadId)
      }
    })

    // Clean up recently added comments
    recentlyAddedComments.forEach((timestamp, commentId) => {
      if (now - timestamp > RECENT_COMMENT_TTL) {
        recentlyAddedComments.delete(commentId)
      }
    })
  }, 60000) // Check every minute
}

export interface ThreadState {
  threads: ThreadWithMetadata[]
  threadComments: ThreadCommentWithLocal[]
  quarantinedPendingComments: unknown[]
  selectedThreadId: string | null
  error: Error | null
  isLoading: boolean
  totalThreads: number
  lastFetchTime: number | null
  setThreads: (threads: Thread[]) => void
  addThread: (thread: Thread) => void
  updateThread: (thread: Thread) => void
  removeThread: (threadId: string) => void
  // Drop threads plus their cached comments (e.g. after a task merge moved
  // or retired them server-side) so the next render refetches fresh data.
  evictThreadData: (threadIds: string[]) => void
  setSelectedThread: (thread: Thread | null) => void
  // Real-time sync methods (UI state management for cross-tab sync)
  setThreadComments: (comments: ThreadCommentWithLocal[]) => void
  addThreadComment: (
    commentDataFromEvent: Partial<ThreadCommentWithLocal> & {
      id: string
      threadId: string
    },
  ) => void
  setThreadCommentLocalStatus: (
    commentId: string,
    status: 'pending' | 'failed' | null,
  ) => void
  removeThreadCommentLocalById: (commentId: string) => void
  removeThreadCommentById: (commentId: string) => void
  updateThreadComment: (comment: ThreadCommentWithLocal) => void
  updateThreadCommentCount: (threadId: string, count: number) => void
  // Comment fetching helper (used for lazy loading)
  fetchThreadCommentsIfNeeded: (
    threadId: string,
    options?: {
      parentCommentId?: string | null
      limit?: number
      page?: number
      forceRefresh?: boolean
    },
  ) => Promise<ThreadComment[]>
  getThreadCommentCacheInfo: (
    threadId: string,
    options?: {
      parentCommentId?: string | null
      limit?: number
      page?: number
    },
  ) => { totalComments: number; isLoading: boolean } | null
  reset: () => void
  handleThreadReactionAdded: (payload: ThreadReactionAddedPayload) => void
  handleThreadReactionRemoved: (payload: ThreadReactionRemovedPayload) => void
  handleCommentReactionAdded: (payload: CommentReactionAddedPayload) => void
  handleCommentReactionRemoved: (payload: CommentReactionRemovedPayload) => void
  reactionMap: Map<
    string,
    {
      id: string
      commentId?: string
      threadId: string
      timestamp: number
    }
  >
  registerReaction: (reaction: ThreadReaction | ThreadCommentReaction) => void
  unregisterReaction: (reactionId: string) => void
  hasReaction: (targetId: string, type: 'thread' | 'comment') => boolean
  broadcastReactionChange: (actionType: SocketEvent, data: any) => void
  cleanupReactions: () => void
  fetchThreadComments: (
    threadId: string,
    options?: { forceRefresh?: boolean },
  ) => Promise<ThreadComment[]>
  cleanupThreads: () => void
  clearCache: () => void
}

const broadcastedMessages = new Map<string, number>()

const recentlyProcessedReactions = new Map<string, number>()

const cleanupBroadcastedMessages = () => {
  const now = Date.now()
  broadcastedMessages.forEach((timestamp, id) => {
    if (now - timestamp > 30000) {
      broadcastedMessages.delete(id)
    }
  })

  recentlyProcessedReactions.forEach((timestamp, id) => {
    if (now - timestamp > 10000) {
      recentlyProcessedReactions.delete(id)
    }
  })
}

if (typeof window !== 'undefined') {
  setInterval(cleanupBroadcastedMessages, 60000)

  // Also set up thread comment cleanup
  setInterval(() => {
    // Clean up thread comments for threads that aren't active anymore
    const store = useThreadStore.getState()
    store.cleanupThreads()
  }, 120000) // Check every 2 minutes
}

export const useThreadStore = create<ThreadState>()(
  persist(
    (set, get) => ({
      threads: [],
      threadComments: [],
      quarantinedPendingComments: [],
      selectedThreadId: null,
      error: null,
      isLoading: false,
      lastFetchTime: null,
      totalThreads: 0,
      reactionMap: new Map(),

      setThreads: threads => set({ threads }),

      setThreadComments: comments => set({ threadComments: comments }),

      addThread: (thread: Thread) => {
        set(state =>
          state.threads.some(t => t.id === thread.id)
            ? state
            : { threads: [thread, ...state.threads] },
        )
      },
      updateThread: (thread: Thread) => {
        set(state =>
          !state.threads.some(t => t.id === thread.id)
            ? state
            : {
                threads: state.threads.map(t =>
                  t.id === thread.id ? { ...thread } : t,
                ),
              },
        )
      },
      removeThread: (threadId: string) => {
        set(state => ({
          threads: state.threads.filter(t => t.id !== threadId),
          selectedThreadId:
            state.selectedThreadId === threadId ? null : state.selectedThreadId,
        }))
      },
      evictThreadData: (threadIds: string[]) => {
        if (threadIds.length === 0) return
        const ids = new Set(threadIds)
        threadCommentCache.forEach((_entry, cacheKey) => {
          for (const threadId of ids) {
            if (cacheKey.startsWith(`thread:${threadId}:`)) {
              threadCommentCache.delete(cacheKey)
              break
            }
          }
        })
        set(state => ({
          threads: state.threads.filter(t => !ids.has(t.id)),
          threadComments: state.threadComments.filter(
            c => !ids.has(c.threadId),
          ),
          selectedThreadId:
            state.selectedThreadId && ids.has(state.selectedThreadId)
              ? null
              : state.selectedThreadId,
        }))
      },
      setSelectedThread: (thread: Thread | null) =>
        set({ selectedThreadId: thread?.id || null }),
      reset: () => {
        // Clear API cache
        threadApi.clearCache?.()

        // Clear local caches
        threadCommentCache.clear()
        recentlyAddedComments.clear()
        broadcastedMessages.clear()
        recentlyProcessedReactions.clear()

        // Reset to default state
        set({
          threads: [],
          threadComments: [],
          selectedThreadId: null,
          isLoading: false,
          error: null,
          lastFetchTime: null,
          totalThreads: 0,
          reactionMap: new Map(),
        })

        // Clear any localStorage or sessionStorage data related to threads
        if (typeof window !== 'undefined') {
          try {
            // Clear active threads tracking
            localStorage.removeItem('activeThreads')

            // Clear other thread-related storage items
            localStorage.removeItem('lastCommentTime')
            localStorage.removeItem('lastCommentThreadId')
            sessionStorage.removeItem('recentComments')
          } catch (e) {
            logger.error('[ThreadStore] Error clearing thread storage:', e)
          }
        }
      },
      addThreadComment: (
        commentDataFromEvent: Partial<ThreadCommentWithLocal> & {
          id: string
          threadId: string
        },
      ) => {
        const state = get()
        const clientMessageId = commentDataFromEvent.clientMessageId || null
        const optimisticMatch = clientMessageId
          ? state.threadComments.find(
              c => c.clientMessageId === clientMessageId,
            )
          : undefined

        if (optimisticMatch && optimisticMatch.id !== commentDataFromEvent.id) {
          recentlyAddedComments.set(commentDataFromEvent.id, Date.now())
          recentlyAddedComments.delete(optimisticMatch.id)

          set(s => {
            const updatedComments = s.threadComments
              .filter(
                comment =>
                  comment.id !== commentDataFromEvent.id ||
                  comment.clientMessageId === clientMessageId,
              )
              .map(comment => {
                if (comment.clientMessageId === clientMessageId) {
                  return {
                    ...comment,
                    ...commentDataFromEvent,
                    localStatus: undefined,
                  } as ThreadCommentWithLocal
                }
                return comment
              })
              .sort((a, b) => {
                const thread = s.threads.find(t => t.id === a.threadId)
                const sortOrder =
                  thread?.threadType === 'COMMENT' ? 'desc' : 'asc'
                return compareThreadComments(a, b, sortOrder)
              })

            const updatedThreads = s.threads.map(th =>
              th.id === commentDataFromEvent.threadId
                ? {
                    ...th,
                    updatedAt: commentDataFromEvent.createdAt
                      ? new Date(commentDataFromEvent.createdAt)
                      : th.updatedAt,
                    _lastAddedCommentId: commentDataFromEvent.id,
                  }
                : th,
            )

            return {
              threadComments: updatedComments,
              threads: updatedThreads,
            }
          })

          return
        }

        // Enhanced duplicate detection
        const isDuplicate = state.threadComments.some(
          c => c.id === commentDataFromEvent.id,
        )
        const isRecentlyAdded = recentlyAddedComments.has(
          commentDataFromEvent.id,
        )

        if (isDuplicate || isRecentlyAdded) {
          // The comment is already known, but the incoming payload (e.g. from
          // ModuleComments re-fetching root comments, or a realtime update) may
          // carry fresher server-authoritative fields. Reconcile them instead of
          // dropping the payload outright — otherwise a stale persisted
          // totalReplies stays at 0 and the parent renders "No replies yet"
          // even though replies exist.
          const freshReplies = commentDataFromEvent.totalReplies
          if (isDuplicate && typeof freshReplies === 'number') {
            set(s => {
              let changed = false
              const threadComments = s.threadComments.map(c => {
                if (c.id !== commentDataFromEvent.id || c.localStatus) return c
                if ((c.totalReplies ?? 0) === freshReplies) return c
                changed = true
                return { ...c, totalReplies: freshReplies }
              })
              return changed ? { threadComments } : s
            })
          }
          return
        }

        // Mark as recently added to prevent duplicate processing
        recentlyAddedComments.set(commentDataFromEvent.id, Date.now())

        // If we have enough data, add the comment directly to the store.
        // Note: content may be an empty string for attachment-only comments —
        // check for presence, not truthiness, otherwise we'd refetch via the
        // org-only /threads/comments/:id endpoint and 403 in the client portal.
        if (
          commentDataFromEvent.content !== undefined &&
          commentDataFromEvent.authorId
        ) {
          set(s => {
            // Final duplicate check
            if (s.threadComments.some(c => c.id === commentDataFromEvent.id)) {
              return s // Return current state, no change
            }

            // If this is a reply, optimistically bump the parent comment's
            // totalReplies so the parent renders its reply thread immediately
            // (and the bumped count is persisted, surviving a refresh). The
            // parent's count is never reconciled by the server merge until a
            // clean refetch, so it must be maintained here.
            const parentId = commentDataFromEvent.parentCommentId
            const baseComments = parentId
              ? s.threadComments.map(c =>
                  c.id === parentId
                    ? { ...c, totalReplies: (c.totalReplies ?? 0) + 1 }
                    : c,
                )
              : s.threadComments

            // Add to comments array and sort based on thread type
            const updatedComments = [
              ...baseComments,
              commentDataFromEvent as ThreadCommentWithLocal,
            ].sort((a, b) => {
              // Find the thread to determine sorting
              const thread = s.threads.find(t => t.id === a.threadId)
              const sortOrder =
                thread?.threadType === 'COMMENT' ? 'desc' : 'asc'
              return compareThreadComments(a, b, sortOrder)
            })

            // Update the parent thread's comment count and timestamp
            const updatedThreads = s.threads.map(th =>
              th.id === commentDataFromEvent.threadId
                ? {
                    ...th,
                    totalComments: th.totalComments + 1,
                    updatedAt: commentDataFromEvent.createdAt
                      ? new Date(commentDataFromEvent.createdAt)
                      : new Date(),
                    _lastAddedCommentId: commentDataFromEvent.id,
                  }
                : th,
            )

            return {
              threadComments: updatedComments,
              threads: updatedThreads,
            }
          })

          return
        }
        threadApi
          .getThreadCommentById(commentDataFromEvent.id)
          .then(fullComment => {
            if (!fullComment) {
              logger.error(
                `[ThreadStore] Failed to fetch full details for comment ${commentDataFromEvent.id}. API returned null or undefined.`,
              )
              return
            }

            // Add the complete comment to the store with duplicate check
            set(s => {
              // Check again for duplicates
              if (s.threadComments.some(c => c.id === fullComment.id)) {
                return s // No change needed
              }

              // If this is a reply, optimistically bump the parent's totalReplies.
              const parentId = fullComment.parentCommentId
              const baseComments = parentId
                ? s.threadComments.map(c =>
                    c.id === parentId
                      ? { ...c, totalReplies: (c.totalReplies ?? 0) + 1 }
                      : c,
                  )
                : s.threadComments

              // Add comment to store and sort based on thread type
              const updatedComments = [
                ...baseComments,
                fullComment as ThreadCommentWithLocal,
              ].sort((a, b) => {
                // Find the thread to determine sorting
                const thread = s.threads.find(t => t.id === a.threadId)
                const sortOrder =
                  thread?.threadType === 'COMMENT' ? 'desc' : 'asc'
                return compareThreadComments(a, b, sortOrder)
              })

              // Update thread metadata
              return {
                threadComments: updatedComments,
                threads: s.threads.map(th =>
                  th.id === fullComment.threadId
                    ? {
                        ...th,
                        totalComments: th.totalComments + 1,
                        updatedAt: fullComment.createdAt
                          ? new Date(fullComment.createdAt)
                          : new Date(),
                        _lastAddedCommentId: fullComment.id,
                      }
                    : th,
                ),
              }
            })
          })
          .catch(error => {
            logger.error(
              `[ThreadStore] Error fetching full details for comment ${commentDataFromEvent.id}:`,
              error,
            )
          })
      },
      setThreadCommentLocalStatus: (
        commentId: string,
        status: 'pending' | 'failed' | null,
      ) => {
        set(state => ({
          threadComments: state.threadComments.map(comment =>
            comment.id === commentId
              ? { ...comment, localStatus: status ?? undefined }
              : comment,
          ),
        }))
      },
      removeThreadCommentLocalById: (commentId: string) => {
        set(state => {
          const comment = state.threadComments.find(c => c.id === commentId)
          if (!comment?.threadId) return state

          const shouldDecrement = !comment.deletedAt
          const threadId = comment.threadId
          const parentId = comment.parentCommentId

          return {
            threadComments: state.threadComments
              .filter(c => c.id !== commentId)
              .map(c =>
                shouldDecrement && parentId && c.id === parentId
                  ? {
                      ...c,
                      totalReplies: Math.max(0, (c.totalReplies ?? 0) - 1),
                    }
                  : c,
              ),
            threads: state.threads.map(t =>
              t.id === threadId
                ? {
                    ...t,
                    totalComments: shouldDecrement
                      ? Math.max(0, t.totalComments - 1)
                      : t.totalComments,
                  }
                : t,
            ),
          }
        })
      },
      removeThreadCommentById: (commentId: string) => {
        const comment = get().threadComments.find(c => c.id === commentId)
        if (!comment?.threadId) return
        const threadId = comment.threadId
        const parentId = comment.parentCommentId
        if (comment.deletedAt) return

        set(state => ({
          threadComments: state.threadComments.map(c => {
            if (c.id === commentId) {
              return { ...c, content: '', deletedAt: new Date() }
            }
            if (parentId && c.id === parentId) {
              return {
                ...c,
                totalReplies: Math.max(0, (c.totalReplies ?? 0) - 1),
              }
            }
            return c
          }),
          threads: state.threads.map(t =>
            t.id === threadId
              ? { ...t, totalComments: Math.max(0, t.totalComments - 1) }
              : t,
          ),
        }))
      },
      updateThreadComment: (updatedComment: ThreadComment) => {
        set(state => {
          const existing = state.threadComments.find(
            c => c.id === updatedComment.id,
          )
          const wasDeleted = existing?.deletedAt
          const isDeleted = !!updatedComment.deletedAt
          const threadId = updatedComment.threadId || existing?.threadId

          const threadComments = existing
            ? state.threadComments.map(c =>
                c.id === updatedComment.id ? updatedComment : c,
              )
            : [...state.threadComments, updatedComment]

          if (!threadId) {
            return { threadComments }
          }

          const threads = state.threads.map(t => {
            if (t.id !== threadId) return t

            if (!existing && !isDeleted) {
              return { ...t, totalComments: Math.max(0, t.totalComments + 1) }
            }

            if (existing && !wasDeleted && isDeleted) {
              return { ...t, totalComments: Math.max(0, t.totalComments - 1) }
            }

            if (existing && wasDeleted && !isDeleted) {
              return { ...t, totalComments: Math.max(0, t.totalComments + 1) }
            }

            return t
          })

          return { threadComments, threads }
        })
      },
      updateThreadCommentCount: (threadId: string, count: number) => {
        set(state => {
          if (!state.threads.some(t => t.id === threadId)) return state
          return {
            threads: state.threads.map(t =>
              t.id === threadId
                ? { ...t, totalComments: Math.max(0, t.totalComments + count) }
                : t,
            ),
          }
        })
      },
      registerReaction: reaction => {
        const reactionId = reaction.id
        let commentId: string | undefined
        let threadId: string | undefined

        if ('commentId' in reaction && reaction.commentId) {
          commentId = reaction.commentId
          if ('threadId' in reaction && reaction.threadId) {
            threadId = reaction.threadId as string
          } else {
            const comment = get().threadComments.find(c => c.id === commentId)
            if (comment) {
              threadId = comment.threadId
            } else {
              logger.error(
                `[ThreadStore] Cannot register reaction ${reactionId}: Comment ${commentId} not found.`,
              )
              return
            }
          }
        } else if ('threadId' in reaction) {
          threadId = reaction.threadId
        }

        if (!threadId) {
          logger.error(
            `[ThreadStore] Cannot register reaction ${reactionId}: threadId missing.`,
          )
          return
        }

        // Skip if already registered
        if (get().reactionMap.has(reactionId)) return

        // Store in reaction map
        const now = Date.now()
        set(state => {
          const newMap = new Map(state.reactionMap)
          newMap.set(reactionId, {
            id: reactionId,
            commentId,
            threadId: threadId!,
            timestamp: now,
          })
          return { reactionMap: newMap }
        })
      },

      unregisterReaction: reactionId => {
        if (!get().reactionMap.has(reactionId)) return
        set(state => {
          const newMap = new Map(state.reactionMap)
          newMap.delete(reactionId)
          return { reactionMap: newMap }
        })
      },

      cleanupReactions: () => {
        const now = Date.now()
        const MAX_AGE = 3600000
        set(state => {
          const newMap = new Map(state.reactionMap)
          let cleaned = 0
          newMap.forEach((_reaction, id) => {
            const storedReaction = newMap.get(id)
            if (storedReaction && now - storedReaction.timestamp > MAX_AGE) {
              newMap.delete(id)
              cleaned++
            }
          })
          if (cleaned > 0) return { reactionMap: newMap }
        })
      },

      hasReaction: (targetId, type) => {
        const { reactionMap } = get()
        return Array.from(reactionMap.values()).some(r =>
          type === 'comment'
            ? r.commentId === targetId
            : r.threadId === targetId && !r.commentId,
        )
      },

      broadcastReactionChange: (actionType, data) => {
        if (
          typeof window === 'undefined' ||
          typeof BroadcastChannel === 'undefined'
        )
          return

        try {
          // Get the entity ID
          const isComment = actionType.includes('COMMENT_')
          const entityId = isComment ? data.threadId : data.threadId || data.id

          if (!entityId) {
            logger.error(
              '[ThreadStore] Cannot broadcast reaction without entityId',
            )
            return
          }

          // Use the same channel name as in useRealtime
          const channel = new BroadcastChannel('scrambl-realtime-sync')

          // Map SocketEvent to appropriate broadcast event type
          let type: string
          switch (actionType) {
            case SocketEvent.THREAD_REACTION_ADDED:
              type = 'thread:reaction_added'
              break
            case SocketEvent.THREAD_REACTION_REMOVED:
              type = 'thread:reaction_removed'
              break
            case SocketEvent.COMMENT_REACTION_ADDED:
              type = 'comment:reaction_added'
              break
            case SocketEvent.COMMENT_REACTION_REMOVED:
              type = 'comment:reaction_removed'
              break
            default:
              type = actionType
          }

          // Build a message that matches the format used by the useRealtime hook
          channel.postMessage({
            type: type,
            entityType: 'thread',
            entityId: entityId,
            data: data,
            timestamp: Date.now(),
            messageId: `reaction-${type}-${entityId}-${data.id}-${Date.now()}`,
            source: 'client',
            tabId: `thread-store-${Date.now()}`,
          })

          channel.close()
        } catch (e) {
          logger.error(
            `[ThreadStore] Error broadcasting reaction ${actionType}:`,
            e,
          )
        }
      },

      handleThreadReactionAdded: (payload: ThreadReactionAddedPayload) => {
        // Skip if we don't have the thread
        if (!get().threads.some(t => t.id === payload.threadId)) {
          return
        }

        // Skip if we already registered this exact reaction
        if (get().reactionMap.has(payload.id)) {
          return
        }

        // Register in our tracking map
        get().registerReaction(payload as ThreadReaction)

        const reactor = createReactorFromPayload(
          payload.creator,
          payload.creatorId,
        )

        // Update the thread with the new reaction using emoji-based reactionGroups
        set(state => ({
          threads: state.threads.map(thread => {
            if (thread.id !== payload.threadId) {
              return thread
            }

            const emoji = payload.emoji
            const currentGroups = normalizeReactionGroups(thread.reactionGroups)
            const existingGroup = currentGroups.find(g => g.emoji === emoji)

            let updatedGroups: ReactionGroup[]
            if (existingGroup) {
              // Add to existing group
              updatedGroups = currentGroups.map(g =>
                g.emoji === emoji
                  ? {
                      ...g,
                      count: g.count + 1,
                      reactors: [...(g.reactors || []), reactor],
                    }
                  : g,
              )
            } else {
              // Create new group
              updatedGroups = [
                ...currentGroups,
                {
                  emoji,
                  nativeEmoji: unifiedToNative(emoji),
                  count: 1,
                  hasReacted: false,
                  reactors: [reactor],
                },
              ]
            }

            const totalReactions = updatedGroups.reduce(
              (sum, g) => sum + g.count,
              0,
            )

            return {
              ...thread,
              reactionGroups: updatedGroups,
              totalReactions,
            }
          }),
        }))
      },

      handleThreadReactionRemoved: (payload: ThreadReactionRemovedPayload) => {
        // Check if we're tracking this thread before processing
        const isThreadLoaded = get().threads.some(
          t => t.id === payload.threadId,
        )
        if (!isThreadLoaded) {
          // Skip processing for threads we're not tracking
          return
        }
        get().unregisterReaction(payload.id)

        // Update the thread using emoji-based reactionGroups
        set(state => ({
          threads: state.threads.map(thread => {
            if (thread.id !== payload.threadId) {
              return thread
            }

            const emoji = payload.emoji
            const currentGroups = normalizeReactionGroups(thread.reactionGroups)

            // Decrement count for the emoji group, remove if count reaches 0
            const updatedGroups = currentGroups
              .map(g =>
                g.emoji === emoji
                  ? {
                      ...g,
                      count: Math.max(0, g.count - 1),
                      reactors: (g.reactors || []).filter(
                        r => r.id !== payload.creatorId,
                      ),
                    }
                  : g,
              )
              .filter(g => g.count > 0)

            const totalReactions = updatedGroups.reduce(
              (sum, g) => sum + g.count,
              0,
            )

            return {
              ...thread,
              reactionGroups: updatedGroups,
              totalReactions,
            }
          }),
        }))
      },

      handleCommentReactionAdded: (payload: CommentReactionAddedPayload) => {
        // Skip if we don't have the related comment
        if (!get().threadComments.some(c => c.id === payload.commentId)) {
          return
        }

        debugLog(`[ThreadStore] Processing comment reaction: ${payload.id}`)

        // Skip if we already registered this exact reaction
        if (get().reactionMap.has(payload.id)) {
          debugLog(
            `[ThreadStore] Skipping already registered reaction: ${payload.id}`,
          )
          return
        }

        // Register in our tracking map
        get().registerReaction(
          payload as ThreadCommentReaction & { threadId: string },
        )

        const reactor = createReactorFromPayload(
          payload.creator,
          payload.creatorId,
        )

        // Update the comment with the new reaction using emoji-based reactionGroups
        set(state => ({
          threadComments: state.threadComments.map(comment => {
            if (comment.id !== payload.commentId) {
              return comment
            }

            const emoji = payload.emoji
            const currentGroups = normalizeReactionGroups(
              comment.reactionGroups,
            )
            const existingGroup = currentGroups.find(g => g.emoji === emoji)

            let updatedGroups: ReactionGroup[]
            if (existingGroup) {
              // Add to existing group
              updatedGroups = currentGroups.map(g =>
                g.emoji === emoji
                  ? {
                      ...g,
                      count: g.count + 1,
                      reactors: [...(g.reactors || []), reactor],
                    }
                  : g,
              )
            } else {
              // Create new group
              updatedGroups = [
                ...currentGroups,
                {
                  emoji,
                  nativeEmoji: unifiedToNative(emoji),
                  count: 1,
                  hasReacted: false, // Will be set based on current user
                  reactors: [reactor],
                },
              ]
            }

            const totalReactions = updatedGroups.reduce(
              (sum, g) => sum + g.count,
              0,
            )

            return {
              ...comment,
              reactionGroups: updatedGroups,
              totalReactions,
            }
          }),
        }))
      },

      handleCommentReactionRemoved: (
        payload: CommentReactionRemovedPayload,
      ) => {
        // Check if the specific comment exists
        const commentExists = get().threadComments.some(
          c => c.id === payload.commentId,
        )
        if (!commentExists) {
          // Skip processing for comments we're not tracking
          return
        }
        get().unregisterReaction(payload.id)

        // Update the comment using emoji-based reactionGroups
        set(state => ({
          threadComments: state.threadComments.map(comment => {
            if (comment.id !== payload.commentId) {
              return comment
            }

            const emoji = payload.emoji
            const currentGroups = normalizeReactionGroups(
              comment.reactionGroups,
            )

            // Decrement count for the emoji group, remove if count reaches 0
            const updatedGroups = currentGroups
              .map(g =>
                g.emoji === emoji
                  ? {
                      ...g,
                      count: Math.max(0, g.count - 1),
                      reactors: (g.reactors || []).filter(
                        r => r.id !== payload.creatorId,
                      ),
                    }
                  : g,
              )
              .filter(g => g.count > 0)

            const totalReactions = updatedGroups.reduce(
              (sum, g) => sum + g.count,
              0,
            )

            return {
              ...comment,
              reactionGroups: updatedGroups,
              totalReactions,
            }
          }),
        }))
      },

      fetchThreadCommentsIfNeeded: async (
        threadId: string,
        options?: {
          parentCommentId?: string | null
          limit?: number
          page?: number
          forceRefresh?: boolean
        },
      ) => {
        const parentCommentId = options?.parentCommentId
        const limit = options?.limit || 12 // Default to display size (6) plus a buffer
        const page = options?.page || 1

        // Create a cache key that includes pagination parameters
        const cacheKey = buildThreadCommentCacheKey(
          threadId,
          parentCommentId,
          page,
          limit,
        )

        // First check if we already have enough comments for this thread in the store
        const existingComments = get().threadComments.filter(c => {
          // Filter by threadId first
          if (c.threadId !== threadId) {
            return false
          }

          // Then filter by parentCommentId if specified
          if (parentCommentId === undefined) {
            return true // Include all comments for this thread
          } else if (parentCommentId === null) {
            return c.parentCommentId === null // Only root comments
          } else {
            return c.parentCommentId === parentCommentId // Only replies to specific comment
          }
        })

        // If we already have enough comments for the current request and forceRefresh is not set, just return them
        if (existingComments.length >= limit && !options?.forceRefresh) {
          return existingComments
        }

        // Check cache, but bypass if forceRefresh is true
        const cachedEntry = !options?.forceRefresh
          ? threadCommentCache.get(cacheKey)
          : null
        const now = Date.now()

        // Use cache if available and fresh
        if (
          cachedEntry &&
          now - cachedEntry.timestamp < COMMENT_CACHE_TTL / 2 &&
          !cachedEntry.isLoading &&
          !options?.forceRefresh
        ) {
          // Add any missing comments to the store and reconcile existing ones
          // with the cached server data (keeps fields like totalReplies fresh).
          if (cachedEntry.comments.length > 0) {
            const newComments = cachedEntry.comments.filter(
              cachedComment =>
                !existingComments.some(c => c.id === cachedComment.id),
            )

            set(state => {
              const serverById = new Map(
                cachedEntry.comments.map(c => [c.id, c] as const),
              )
              let didUpdateExisting = false
              const reconciled = state.threadComments.map(existing => {
                const fresh = serverById.get(existing.id)
                if (!fresh || existing.localStatus) return existing
                didUpdateExisting = true
                return { ...existing, ...fresh } as ThreadCommentWithLocal
              })

              if (newComments.length === 0 && !didUpdateExisting) {
                return state
              }

              const allComments = [...reconciled, ...newComments]

              // Sort all comments based on their thread type
              const sortedComments = allComments.sort((a, b) => {
                // If comments are from the same thread, sort by thread type
                if (a.threadId === b.threadId) {
                  const thread = state.threads.find(t => t.id === a.threadId)
                  const sortOrder =
                    thread?.threadType === 'COMMENT' ? 'desc' : 'asc'
                  return compareThreadComments(a, b, sortOrder)
                }

                // Default sorting by creation time if different threads
                return (
                  new Date(a.createdAt).getTime() -
                  new Date(b.createdAt).getTime()
                )
              })

              return { threadComments: sortedComments }
            })
          }

          return cachedEntry.comments
        }

        // If there's an ongoing fetch, don't start another one
        if (cachedEntry?.isLoading) {
          return existingComments
        }

        // Mark as loading in cache
        threadCommentCache.set(cacheKey, {
          comments: cachedEntry?.comments || [],
          timestamp: cachedEntry?.timestamp || 0,
          totalComments: cachedEntry?.totalComments || 0,
          isLoading: true,
        })

        try {
          // For replies, we always want them in chronological order (oldest first)
          // For root comments on page 1, we want newest first
          const sortOrder =
            (parentCommentId && parentCommentId !== null) || page > 1
              ? 'asc' // Replies or older pages: chronological order
              : 'desc' // Root comments, first page: reverse chronological

          // Fetch from API
          const response = await threadApi.getThreadComments({
            threadId,
            parentCommentId: parentCommentId,
            limit,
            page: options?.page || 1,
            sortBy: 'sequence',
            sortOrder: sortOrder,
            skipCache: options?.forceRefresh,
          })

          // Process response
          const comments = Array.isArray(response) ? response : response.data
          const totalCount = Array.isArray(response)
            ? comments.length
            : response.meta?.totalItems || comments.length

          // If it's page 1 of root comments, we need to reverse since they came in desc order
          // This ensures chronological order is maintained in the store
          // For replies, we don't need to reverse as they already come in chronological order
          const processedComments =
            sortOrder === 'desc' ? [...comments].reverse() : comments

          // Update cache
          threadCommentCache.set(cacheKey, {
            comments: processedComments,
            timestamp: now,
            totalComments: totalCount,
            isLoading: false,
          })

          // Register thread as active
          if (typeof window !== 'undefined') {
            try {
              const activeThreads = JSON.parse(
                localStorage.getItem('activeThreads') || '{}',
              )
              activeThreads[threadId] = now
              localStorage.setItem(
                'activeThreads',
                JSON.stringify(activeThreads),
              )
            } catch (e) {
              logger.error('[ThreadStore] Error updating active threads:', e)
            }
          }

          // Update store with new comments, avoiding duplicates.
          // Also reconcile EXISTING comments with the freshly fetched server
          // data so server-authoritative fields (e.g. totalReplies) self-heal a
          // stale persisted cache. Without this, a parent comment whose
          // totalReplies was never incremented stays at 0 forever and its
          // replies render as "No replies yet" even though they exist.
          if (processedComments.length > 0) {
            set(state => {
              const serverById = new Map(
                processedComments.map(c => [c.id, c] as const),
              )
              let didUpdateExisting = false
              const reconciled = state.threadComments.map(existing => {
                const fresh = serverById.get(existing.id)
                // Don't clobber optimistic comments still in flight.
                if (!fresh || existing.localStatus) return existing
                didUpdateExisting = true
                return { ...existing, ...fresh } as ThreadCommentWithLocal
              })

              const newComments = processedComments.filter(
                newComment =>
                  !state.threadComments.some(
                    existing => existing.id === newComment.id,
                  ),
              )

              if (newComments.length === 0 && !didUpdateExisting) {
                return state // No change needed
              }

              const allComments = [...reconciled, ...newComments]

              // Sort all comments based on their thread type
              const sortedComments = allComments.sort((a, b) => {
                // If comments are from the same thread, sort by thread type
                if (a.threadId === b.threadId) {
                  const thread = state.threads.find(t => t.id === a.threadId)
                  const sortOrder =
                    thread?.threadType === 'COMMENT' ? 'desc' : 'asc'
                  return compareThreadComments(a, b, sortOrder)
                }

                // Default sorting by creation time if different threads
                return (
                  new Date(a.createdAt).getTime() -
                  new Date(b.createdAt).getTime()
                )
              })

              return {
                threadComments: sortedComments,
              }
            })
          }
          return processedComments
        } catch (error) {
          logger.error(
            `[ThreadStore] Error fetching comments for thread ${threadId}:`,
            error,
          )

          // Clear loading state in cache
          const existingEntry = threadCommentCache.get(cacheKey)
          if (existingEntry) {
            threadCommentCache.set(cacheKey, {
              ...existingEntry,
              isLoading: false,
            })
          }

          return existingComments
        }
      },

      getThreadCommentCacheInfo: (
        threadId: string,
        options?: {
          parentCommentId?: string | null
          limit?: number
          page?: number
        },
      ) => {
        const parentCommentId = options?.parentCommentId
        const limit = options?.limit || 12
        const page = options?.page || 1
        const cacheKey = buildThreadCommentCacheKey(
          threadId,
          parentCommentId,
          page,
          limit,
        )
        const entry = threadCommentCache.get(cacheKey)
        if (entry) {
          return {
            totalComments: entry.totalComments,
            isLoading: entry.isLoading,
          }
        }

        const parentKey =
          parentCommentId === null ? 'root' : parentCommentId || 'all'
        const prefix = `thread:${threadId}:parent:${parentKey}:`
        let newest: CommentCacheEntry | null = null

        threadCommentCache.forEach((value, key) => {
          if (!key.startsWith(prefix)) {
            return
          }
          if (!newest || value.timestamp > newest.timestamp) {
            newest = value
          }
        })

        if (!newest) {
          return null
        }
        return {
          totalComments: newest.totalComments,
          isLoading: newest.isLoading,
        }
      },

      fetchThreadComments: async (
        threadId: string,
        options: { forceRefresh?: boolean } = {},
      ) => {
        try {
          const now = Date.now()
          const state = get()

          // 1. Check cache first for efficiency
          const cachedEntry = threadCommentCache.get(threadId)
          const useCache =
            !options.forceRefresh &&
            cachedEntry &&
            now - cachedEntry.timestamp < COMMENT_CACHE_TTL &&
            !cachedEntry.isLoading

          if (useCache) {
            // Return cached comments immediately
            return cachedEntry.comments
          }

          // Mark as loading in cache
          threadCommentCache.set(threadId, {
            comments: cachedEntry?.comments || [],
            timestamp: cachedEntry?.timestamp || 0,
            totalComments: cachedEntry?.totalComments || 0,
            isLoading: true,
          })

          // 2. Fetch from API
          const response = await threadApi.getThreadComments({
            threadId,
            parentCommentId: null,
            sortBy: 'sequence',
            sortOrder: 'asc', // Chronological order (oldest first)
            limit: 8, // Fetch enough for initial display plus a buffer
          })

          // Extract comments from response
          const comments = Array.isArray(response) ? response : response.data
          const totalCount = Array.isArray(response)
            ? comments.length
            : response.meta?.totalItems || comments.length

          // 3. Update cache with fresh data
          threadCommentCache.set(threadId, {
            comments,
            timestamp: now,
            totalComments: totalCount,
            isLoading: false,
          })

          // 4. Update store state with proper sorting
          set(state => {
            const filteredComments = state.threadComments.filter(
              c => c.threadId !== threadId,
            )
            const allComments = [...filteredComments, ...comments]

            // Sort all comments based on their thread type
            const sortedComments = allComments.sort((a, b) => {
              const threadA = state.threads.find(t => t.id === a.threadId)
              const threadB = state.threads.find(t => t.id === b.threadId)

              // If both comments are from the same thread, sort by thread type
              if (a.threadId === b.threadId) {
                const thread = threadA
                const sortOrder =
                  thread?.threadType === 'COMMENT' ? 'desc' : 'asc'
                return compareThreadComments(a, b, sortOrder)
              }

              // Default sorting by creation time if different threads
              return (
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
              )
            })

            return { threadComments: sortedComments }
          })

          // Register this thread as recently active (to prevent aggressive cleanup)
          if (typeof window !== 'undefined') {
            try {
              const activeThreads = JSON.parse(
                localStorage.getItem('activeThreads') || '{}',
              )
              activeThreads[threadId] = now
              localStorage.setItem(
                'activeThreads',
                JSON.stringify(activeThreads),
              )
            } catch (e) {
              logger.error('[ThreadStore] Error updating active threads:', e)
            }
          }

          return comments
        } catch (error) {
          logger.error(
            `[ThreadStore] Error fetching comments for thread ${threadId}:`,
            error,
          )

          // Clear loading state in cache
          const existingEntry = threadCommentCache.get(threadId)
          if (existingEntry) {
            threadCommentCache.set(threadId, {
              ...existingEntry,
              isLoading: false,
            })
          }

          // Return any existing comments as fallback
          return get().threadComments.filter(c => c.threadId === threadId)
        }
      },

      cleanupThreads: () => {
        // Get all threads that currently have comments loaded
        const allThreads = new Set<string>()
        const latestActivityByThread = new Map<string, number>()
        get().threadComments.forEach(comment => {
          allThreads.add(comment.threadId)

          const activityTime =
            comment.updatedAt ||
            comment.editedAt ||
            comment.deletedAt ||
            comment.createdAt
          const activityMs = activityTime ? new Date(activityTime).getTime() : 0
          const currentMax = latestActivityByThread.get(comment.threadId) || 0
          if (activityMs > currentMax) {
            latestActivityByThread.set(comment.threadId, activityMs)
          }
        })

        // Track which threads are currently being viewed or were recently viewed
        const activeThreadIds = new Set<string>()

        // Get currently selected thread ID (if any)
        const selectedThreadId = get().selectedThreadId
        if (selectedThreadId) {
          activeThreadIds.add(selectedThreadId)
        }
        const selectedUiThreadId = useThreadUIStore.getState().selectedThreadId
        if (selectedUiThreadId) {
          activeThreadIds.add(selectedUiThreadId)
        }

        // Check which threads are actually inactive
        const threadsToCleanup: string[] = []

        const now = Date.now()

        allThreads.forEach(threadId => {
          // Add additional checks to ensure a thread is truly inactive
          // Only clean up if:
          // 1. Thread is not the selected thread
          // 2. Thread is not active or visible
          // 3. Thread doesn't have recent activity

          if (
            !renderedThreadRefCounts.has(threadId) &&
            !activeThreadIds.has(threadId) &&
            !isEntityActive('threads', threadId) &&
            !isEntityVisible('threads', threadId) &&
            // Add time-based check: only clean threads inactive after the comment cache window
            !get().threads.some(
              t =>
                t.id === threadId &&
                now - new Date(t.updatedAt).getTime() < COMMENT_CACHE_TTL,
            ) &&
            !(
              latestActivityByThread.has(threadId) &&
              now - (latestActivityByThread.get(threadId) || 0) <
                COMMENT_CACHE_TTL
            )
          ) {
            threadsToCleanup.push(threadId)
          }
        })

        if (threadsToCleanup.length === 0) {
          return
        }

        // Remove comments for inactive threads
        set(state => ({
          threadComments: state.threadComments.filter(
            comment => !threadsToCleanup.includes(comment.threadId),
          ),
        }))

        // Clean up thread reaction map entries for these threads
        const { reactionMap } = get()
        const reactionsToRemove: string[] = []

        reactionMap.forEach((data, id) => {
          if (threadsToCleanup.includes(data.threadId)) {
            reactionsToRemove.push(id)
          }
        })

        if (reactionsToRemove.length > 0) {
          set(state => {
            const newMap = new Map(state.reactionMap)
            reactionsToRemove.forEach(id => newMap.delete(id))
            return { reactionMap: newMap }
          })
        }
      },

      // Get fresh thread metadata from API to update counts
      getThreadMetadata: async (threadId?: string) => {
        const selectedId = threadId || get().selectedThreadId

        if (!selectedId) {
          return null
        }

        try {
          const freshThread = await threadApi.getThread(selectedId)

          if (freshThread) {
            // Update the thread in the store with fresh data
            set(state => ({
              threads: state.threads.map(thread =>
                thread.id === selectedId
                  ? { ...thread, ...freshThread }
                  : thread,
              ),
            }))

            return freshThread
          }

          return null
        } catch (error) {
          logger.error(`[ThreadStore] Error refreshing thread metadata:`, error)
          return null
        }
      },

      clearCache: () => {
        // Clear API cache
        threadApi.clearCache?.()

        // Clear local caches
        threadCommentCache.clear()
        recentlyAddedComments.clear()

        // Reset comment data in store
        set(state => ({
          threadComments: [],
          lastFetchTime: null,
        }))
      },
    }),
    {
      name: 'thread-store',
      version: 2,
      // Historical pending messages lack immutable portal/partner/visibility
      // provenance. Retain them for explicit recovery; never adopt/replay them.
      migrate: persisted => {
        const state = persisted as {
          pendingComments?: unknown[]
          quarantinedPendingComments?: unknown[]
        } | null
        return {
          quarantinedPendingComments: [
            ...(Array.isArray(state?.quarantinedPendingComments)
              ? state.quarantinedPendingComments
              : []),
            ...(Array.isArray(state?.pendingComments)
              ? state.pendingComments
              : []),
          ],
        }
      },
      merge: (persisted, current) => {
        const state = persisted as {
          pendingComments?: unknown[]
          quarantinedPendingComments?: unknown[]
        } | null
        return {
          ...current,
          threads: [],
          threadComments: [],
          selectedThreadId: null,
          quarantinedPendingComments: [
            ...(Array.isArray(state?.quarantinedPendingComments)
              ? state.quarantinedPendingComments
              : []),
            ...(Array.isArray(state?.pendingComments)
              ? state.pendingComments
              : []),
          ],
        }
      },
      // Server replies belong in session-owned queries, never ownerless disk caches.
      partialize: state => ({
        quarantinedPendingComments: state.quarantinedPendingComments,
      }),
    },
  ),
)
